import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "../../../Actions/Axios";
import { getHttpUrl } from "../../../Utils/config";
import ChartBarSVG from "../../../Assets/svg/ChartBarSVG.svg";
import {
  TrendingUp,
  Users,
  ThumbsUp,
  ThumbsDown,
  BarChart2,
  FileText,
} from "lucide-react";

class Analytics extends Component {
  state = {
    loading: true,
    error: null,
    surveys: [],
    overview: {
      total_respondents: 0,
      positive_sentiment_pct: 0,
      negative_sentiment_pct: 0,
      sentiment_score: 0,
      total_surveys: 0,
    },
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    const baseURL = getHttpUrl("");
    const token = sessionStorage.getItem("access");
    if (!token) {
      this.setState({ loading: false, error: "Not authenticated" });
      return;
    }
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      this.setState({ loading: true, error: null });
      const [overviewRes, comparisonRes] = await Promise.all([
        axios.get(`${baseURL}/api/analytics/overview/`, config),
        axios.get(`${baseURL}/api/analytics/surveys-comparison/?limit=10`, config),
      ]);
      this.setState({
        overview: overviewRes.data,
        surveys: comparisonRes.data.surveys || [],
        loading: false,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.error || "Failed to load analytics data",
        loading: false,
      });
    }
  };

  renderKPICards() {
    const { overview, surveys } = this.state;
    const avgScore =
      surveys.length
        ? (
            surveys.reduce((s, sv) => s + (sv.sentiment_score || 0), 0) /
            surveys.length
          ).toFixed(1)
        : "0.0";
    const scoreNum = parseFloat(avgScore);

    const cards = [
      {
        label: "Surveys Analyzed",
        value: String(overview.total_surveys || surveys.length || 0),
        sub: "completed analyses",
        color: "text-blue-600",
        border: "border-blue-100",
        icon: <FileText className="w-4 h-4 text-blue-400" />,
      },
      {
        label: "Total Responses",
        value: (overview.total_respondents || 0).toLocaleString(),
        sub: "across all surveys",
        color: "text-indigo-600",
        border: "border-indigo-100",
        icon: <Users className="w-4 h-4 text-indigo-400" />,
      },
      {
        label: "Avg Positive",
        value: `${(overview.positive_sentiment_pct || 0).toFixed(1)}%`,
        sub: "average across surveys",
        color: "text-green-600",
        border: "border-green-100",
        icon: <ThumbsUp className="w-4 h-4 text-green-400" />,
      },
      {
        label: "Avg Negative",
        value: `${(overview.negative_sentiment_pct || 0).toFixed(1)}%`,
        sub: "average across surveys",
        color: "text-red-500",
        border: "border-red-100",
        icon: <ThumbsDown className="w-4 h-4 text-red-400" />,
      },
      {
        label: "Avg Sentiment Score",
        value: `${scoreNum >= 0 ? "+" : ""}${avgScore}`,
        sub: "positive − negative",
        color: scoreNum >= 0 ? "text-green-600" : "text-red-500",
        border: scoreNum >= 0 ? "border-green-100" : "border-red-100",
        icon: <TrendingUp className="w-4 h-4 text-gray-400" />,
      },
    ];

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((card) => (
          <div
            key={card.label}
            className={`bg-white rounded-xl border ${card.border} px-4 py-3 flex flex-col gap-1 shadow-sm`}
          >
            <div className="flex items-center gap-1.5">
              {card.icon}
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide leading-tight">
                {card.label}
              </span>
            </div>
            <span className={`text-2xl font-bold ${card.color}`}>{card.value}</span>
            <span className="text-[11px] text-gray-400">{card.sub}</span>
          </div>
        ))}
      </div>
    );
  }

  renderSentimentChart() {
    const { surveys } = this.state;
    if (!surveys.length) return null;

    const display = [...surveys].reverse().slice(-8);

    return (
      <div className="bg-white rounded-xl border border-blue-100 px-5 py-4 flex flex-col gap-3 shadow-sm">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-semibold text-gray-700">Sentiment by Survey</span>
        </div>
        <div className="flex flex-col gap-2.5">
          {display.map((sv) => {
            const name =
              sv.original_filename?.replace(/\.(csv|xlsx?)$/i, "") ||
              `Survey ${sv.analysis_id}`;
            const short = name.length > 24 ? name.slice(0, 24) + "…" : name;
            return (
              <div key={sv.analysis_id} className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span
                    className="text-xs text-gray-600 font-medium truncate max-w-[160px]"
                    title={name}
                  >
                    {short}
                  </span>
                  <span className="text-[10px] text-gray-400 ml-2 shrink-0">
                    {sv.total_responses} resp.
                  </span>
                </div>
                <div className="w-full h-2.5 rounded-full bg-gray-100 overflow-hidden flex">
                  <div
                    className="h-full bg-green-400"
                    style={{ width: `${sv.positive_pct}%` }}
                    title={`${sv.positive_pct}% positive`}
                  />
                  <div
                    className="h-full bg-amber-300"
                    style={{ width: `${sv.neutral_pct}%` }}
                    title={`${sv.neutral_pct}% neutral`}
                  />
                  <div
                    className="h-full bg-red-400"
                    style={{ width: `${sv.negative_pct}%` }}
                    title={`${sv.negative_pct}% negative`}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3 text-[11px] text-gray-400 mt-1">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
            Positive
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-300 inline-block" />
            Neutral
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
            Negative
          </span>
        </div>
      </div>
    );
  }

  renderVolumeChart() {
    const { surveys } = this.state;
    if (!surveys.length) return null;

    const display = [...surveys].reverse().slice(-8);
    const maxResp = Math.max(...display.map((s) => s.total_responses), 1);

    return (
      <div className="bg-white rounded-xl border border-indigo-100 px-5 py-4 flex flex-col gap-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-semibold text-gray-700">Response Volume</span>
        </div>
        <div className="flex items-end gap-2 h-[120px]">
          {display.map((sv) => {
            const pct = Math.round((sv.total_responses / maxResp) * 100);
            const name =
              sv.original_filename?.replace(/\.(csv|xlsx?)$/i, "") ||
              `Survey ${sv.analysis_id}`;
            return (
              <div
                key={sv.analysis_id}
                className="flex flex-col items-center flex-1 group gap-1"
                title={`${name}: ${sv.total_responses} responses`}
              >
                <span className="text-[9px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  {sv.total_responses}
                </span>
                <div
                  className="w-full rounded-t-md bg-gradient-to-t from-indigo-500 to-indigo-400 transition-all"
                  style={{ height: `${Math.max(pct * 1.1, 4)}px` }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex">
          {display.map((sv, i) => {
            const label =
              sv.original_filename
                ?.replace(/\.(csv|xlsx?)$/i, "")
                .slice(0, 6) || String(i + 1);
            return (
              <span
                key={sv.analysis_id}
                className="flex-1 text-[9px] text-gray-400 text-center truncate"
              >
                {label}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  renderSurveysTable() {
    const { surveys } = this.state;
    if (!surveys.length) return null;

    const thClass =
      "text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap";

    return (
      <div className="bg-white rounded-xl border border-blue-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
          <h3 className="text-sm font-semibold text-gray-700">Your Surveys</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-100">
                <th className={thClass}>Survey</th>
                <th className={thClass}>Date</th>
                <th className={thClass}>Responses</th>
                <th className={thClass}>Positive</th>
                <th className={thClass}>Negative</th>
                <th className={thClass}>Score</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((sv, idx) => {
                const name = sv.original_filename || `Survey ${sv.analysis_id}`;
                const score = sv.sentiment_score || 0;
                return (
                  <tr
                    key={sv.analysis_id}
                    className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50/30"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-medium text-gray-800 truncate max-w-[220px] block"
                        title={name}
                      >
                        {name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {sv.created_at || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-gray-700">
                      {(sv.total_responses || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-green-400 rounded-full"
                            style={{ width: `${sv.positive_pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-green-600 font-medium w-9">
                          {sv.positive_pct}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-red-400 rounded-full"
                            style={{ width: `${sv.negative_pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-red-500 font-medium w-9">
                          {sv.negative_pct}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs font-bold ${
                          score >= 0 ? "text-green-600" : "text-red-500"
                        }`}
                      >
                        {score >= 0 ? "+" : ""}
                        {score}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderEmptyState() {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-20">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center"
          style={{
            background:
              "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)",
          }}
        >
          <img src={ChartBarSVG} alt="Analytics" className="w-10 h-10" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-600">
            No surveys analyzed yet
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Upload and analyze a survey to start comparing
          </p>
          <button
            onClick={() => (window.location.hash = "#/dashboard")}
            className="mt-4 flex items-center gap-1.5 px-4 py-2 mx-auto rounded-lg bg-gradient-to-r from-primary-blue to-blue-400 text-white text-sm font-medium hover:from-blue-600 hover:to-blue-500 transition-all font-inter shadow-sm"
          >
            Go to SmartSurvey
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, surveys } = this.state;

    return (
      <div
        className="relative w-full h-full flex flex-col border-l overflow-y-auto"
        style={{
          background:
            "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
        }}
      >
        <div className="w-full flex flex-col px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">

          <div className="flex flex-col gap-1">
            <h1 className="font-SFPro font-bold text-2xl text-gray-900">
              Survey Comparison
            </h1>
            <p className="text-sm text-gray-400">
              Compare sentiment and response patterns across all your surveys
            </p>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-gray-500 text-sm">Loading analytics…</div>
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
              <p className="text-red-700 text-sm">{error}</p>
              <button
                onClick={this.fetchData}
                className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && surveys.length === 0 && this.renderEmptyState()}

          {!loading && !error && surveys.length > 0 && (
            <>
              {this.renderKPICards()}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {this.renderSentimentChart()}
                {this.renderVolumeChart()}
              </div>
              {this.renderSurveysTable()}
            </>
          )}

        </div>
      </div>
    );
  }
}

Analytics.propTypes = {};
const mapStateToProps = () => ({});
export default connect(mapStateToProps, {})(Analytics);
